import {
	Component,
	EventEmitter,
	inject,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { Router } from '@angular/router';
import dayjs from 'dayjs';

import { ApplicationService } from '@lmc-app/core/services/application.service';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ApplicationCall } from '@lmc-app/shared/interface/application-call.interface';
import { CommonDialog } from '@lmc-app/shared/components/dialog/common-dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { Applicant } from '@lmc-app/shared/enum/applicant.enum';
import { DateUtil } from '@lmc-app/shared/utils/date.util';
import { ApplicationCallPurpose } from '@lmc-app/shared/enum/application-call-purpose.enum';
import { ApplicationCallStatus } from '@lmc-app/shared/enum/application-call-status.enum';
import { ApplicationCallRequest } from '@lmc-app/shared/interface/request/application-call.request';
import { User } from '@lmc-app/shared/interface/user.interface';

@Component({
	selector: 'app-scheduled-loan-interviews',
	templateUrl: './scheduled-loan-interviews.component.html',
	styleUrls: ['./scheduled-loan-interviews.component.scss'],
})
export class ScheduledLoanInterviewsComponent implements OnInit {
	@Output() updateCallCount = new EventEmitter<number>();
	@Input() currentUser: User;
	@Output() currentUserChange = new EventEmitter<any>();

	displayedColumnsApplicationCalls: string[] = [
		'applicant',
		'subject',
		'purpose',
		'dateTime',
		'status',
		'ownedBy',
		'action',
	];

	dataSourceApplicationCalls = new MatTableDataSource<ApplicationCall>();

	// Loading states
	isLoading = true;
	hasError = false;
	errorMessage = '';

	protected readonly Applicant = Applicant;
	protected readonly DateUtil = DateUtil;
	protected readonly ApplicationCallPurpose = ApplicationCallPurpose;
	protected readonly ApplicationCallStatus = ApplicationCallStatus;

	private readonly _dialog = inject(MatDialog);
	private readonly _snackBar = inject(MatSnackBar);
	private readonly _router = inject(Router);

	constructor(private readonly applicationService: ApplicationService) {}

	// Getter methods for template
	get showStateDisplay(): boolean {
		return (
			this.isLoading ||
			this.hasError ||
			this.dataSourceApplicationCalls.data.length === 0
		);
	}

	get stateType(): 'loading' | 'empty' | 'error' {
		if (this.isLoading) return 'loading';
		if (this.hasError) return 'error';
		return 'empty';
	}

	get showTable(): boolean {
		return (
			!this.isLoading &&
			!this.hasError &&
			this.dataSourceApplicationCalls.data.length > 0
		);
	}

	ngOnInit() {
		this.getCalls();
	}

	getCalls() {
		this.isLoading = true;
		this.hasError = false;
		this.errorMessage = '';

		const todayStart = dayjs().startOf('day').toISOString();
		const status = 'SCHEDULED';
		const purpose = 'READY_FOR_LOAN_INTERVIEW';

		this.applicationService.queryApplicationCalls(null, todayStart, null, status, purpose).subscribe({
			next: (res) => {
				this.dataSourceApplicationCalls.data = res || [];
				this.isLoading = false;
				this.updateCallCount.emit(res.length);
			},
			error: (error) => {
				console.error(error);
				this.hasError = true;
				this.errorMessage = 'Failed to load calls';
				this.isLoading = false;
				this._snackBar.open(
					error,
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
				this.updateCallCount.emit(0);
			},
		});
	}

	private updateLocalData(updatedCall: ApplicationCall) {
		const data = [...this.dataSourceApplicationCalls.data];
		const index = data.findIndex(call => call.uuid === updatedCall.uuid);

		if (index !== -1) {
			data[index] = { ...updatedCall };
			this.dataSourceApplicationCalls.data = data;

			// Update call count if status changed to completed or cancelled
			if (updatedCall.status === ApplicationCallStatus.COMPLETED ||
				updatedCall.status === ApplicationCallStatus.CANCELLED) {
				const activeCallsCount = data.filter(call =>
					call.status !== ApplicationCallStatus.COMPLETED &&
					call.status !== ApplicationCallStatus.CANCELLED
				).length;
				this.updateCallCount.emit(activeCallsCount);
			}
		}
	}

	completeCall(applicationCall: ApplicationCall) {
		this._dialog
			.open(CommonDialog, {
				data: {
					title: 'Are you sure?',
					content: 'Complete this call?',
				},
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					const originalStatus = applicationCall.status;
					applicationCall.status = ApplicationCallStatus.COMPLETED;

					// Update local data immediately for better UX
					this.updateLocalData(applicationCall);

					const request: ApplicationCallRequest = {
						status: ApplicationCallStatus.COMPLETED,
						userId: this.currentUser.employeeID,
					}

					this.applicationService
						.updateApplicationCall(applicationCall.applicationId, applicationCall.uuid, request)
						.subscribe({
							next: () => {
								// Refresh data from server to ensure consistency
								this.getCalls();
							},
							error: (error) => {
								// Revert local change on error
								applicationCall.status = originalStatus;
								this.updateLocalData(applicationCall);

								console.error(error);
								this._snackBar.open(
									error,
									SnackbarConstants.ACTION_DISMISS,
									SnackbarConstants.DURATION_DISMISS_SHORT
								);
							},
						});
				}
			});
	}

	cancelCall(applicationCall: ApplicationCall) {
		this._dialog
			.open(CommonDialog, {
				data: { title: 'Are you sure?', content: 'Cancel this call?' },
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					const originalStatus = applicationCall.status;
					applicationCall.status = ApplicationCallStatus.CANCELLED;

					// Update local data immediately for better UX
					this.updateLocalData(applicationCall);

					const request: ApplicationCallRequest = {
						status: ApplicationCallStatus.CANCELLED,
						userId: this.currentUser.employeeID,
					}

					this.applicationService
						.updateApplicationCall(applicationCall.applicationId, applicationCall.uuid, request)
						.subscribe({
							next: () => {
								// Refresh data from server to ensure consistency
								this.getCalls();
							},
							error: (error) => {
								// Revert local change on error
								applicationCall.status = originalStatus;
								this.updateLocalData(applicationCall);

								console.error(error);
								this._snackBar.open(
									error,
									SnackbarConstants.ACTION_DISMISS,
									SnackbarConstants.DURATION_DISMISS_SHORT
								);
							},
						});
				}
			});
	}

	onClicked(applicationCall: ApplicationCall) {
		this._router.navigate(['/applications/summary', applicationCall.applicationId]);
	}
}
